import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "introductory-econometrics-ols-assumptions-proofs-du",
  title: "Introductory Econometrics & OLS Regression: Gauss-Markov Theorem, Proofs & Exam Numericals",
  description: "Comprehensive 4,500-word masterclass on Introductory Econometrics and Ordinary Least Squares (OLS) for DU Economics students. Covers algebraic derivations of beta_0 and beta_1, Gauss-Markov BLUE proofs, R-squared variance decomposition, hypothesis testing (t and F-tests), and 3 fully worked numerical problems.",
  pubDate: "2026-08-14",
  updatedDate: "2026-08-14",
  authorSlug: "uday-kumar",
  category: "subject-explainers",
  tags: ["econometrics", "ols-regression", "gauss-markov-theorem", "du-economics", "hypothesis-testing"],
  readMinutes: 26,
  heroEyebrow: "Subject Explainers · Econometrics",
};

export function Body() {
  return (
    <>
      <p>
        <strong>Introductory Econometrics</strong> is widely recognized as the most mathematically demanding and conceptually rewarding core course in the Delhi University B.A. (Hons) Economics curriculum (Semester IV Core). It bridges economic theory with empirical data, allowing researchers to quantify relationships, test behavioral hypotheses, and forecast economic phenomena.
      </p>

      <p>
        At the heart of classical econometrics lies the <strong>Ordinary Least Squares (OLS)</strong> estimation method. In semester examinations, derivations of OLS estimators, variance proofs under the Classical Linear Regression Model (CLRM), the <strong>Gauss-Markov Theorem (BLUE proof)</strong>, and ANOVA / R-squared variance decompositions constitute over 40% of the question paper.
      </p>

      <p>
        In this 4,500-word masterclass guide, we derive the simple and multiple OLS estimators from first principles, step through the 7 Classical Linear Regression Model assumptions, prove unbiasedness and minimum variance (Gauss-Markov Theorem), establish hypothesis testing mechanics (t-statistic, p-values, F-tests), and solve 3 complete examination numerical problems.
      </p>

      <h2>1. The Simple Linear Regression Model (SLRM) Setup</h2>
      <p>
        The Population Regression Function (PRF) describes the stochastic relationship between a dependent variable Y and an explanatory variable X:
      </p>

      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        Y_i = beta_0 + beta_1 * X_i + u_i,   for i = 1, 2, ..., n
      </div>
      <ul>
        <li><strong>Y_i:</strong> Dependent (explained / response) variable.</li>
        <li><strong>X_i:</strong> Independent (explanatory / regressor) variable.</li>
        <li><strong>beta_0:</strong> Population intercept (value of Y when X = 0).</li>
        <li><strong>beta_1:</strong> Population slope parameter (change in E[Y] per unit change in X: dE[Y]/dX = beta_1).</li>
        <li><strong>u_i:</strong> Stochastic error term (unobserved factors affecting Y).</li>
      </ul>

      <h3>1.1 Sample Regression Function (SRF) &amp; Residuals</h3>
      <p>
        Because we observe only a sample of size n from the population, we estimate the PRF using the Sample Regression Function:
      </p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        Y_hat_i = beta_0_hat + beta_1_hat * X_i
      </div>
      <p>
        The sample residual <strong>e_i</strong> is the difference between the observed value and the fitted value:
      </p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        e_i = Y_i - Y_hat_i = Y_i - (beta_0_hat + beta_1_hat * X_i)
      </div>

      <h2>2. Mathematical Derivation of OLS Estimators</h2>
      <p>
        The Ordinary Least Squares criterion chooses parameter estimates (beta_0_hat, beta_1_hat) that <strong>minimize the Residual Sum of Squares (RSS)</strong>:
      </p>

      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        Minimize S(beta_0_hat, beta_1_hat) = Sum(e_i^2) = Sum[ (Y_i - beta_0_hat - beta_1_hat * X_i)^2 ]
      </div>

      <h3>2.1 First-Order Conditions (Normal Equations)</h3>
      <p>Take partial derivatives of S with respect to beta_0_hat and beta_1_hat and set them equal to zero:</p>

      <p><strong>Derivative with respect to beta_0_hat:</strong></p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        dS/d(beta_0_hat) = -2 * Sum(Y_i - beta_0_hat - beta_1_hat * X_i) = 0<br />
        Sum(Y_i) - n * beta_0_hat - beta_1_hat * Sum(X_i) = 0
      </div>
      <p>Divide by n (using sample means Y_bar = Sum(Y)/n and X_bar = Sum(X)/n):</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        Normal Equation 1: beta_0_hat = Y_bar - beta_1_hat * X_bar
      </div>

      <p><strong>Derivative with respect to beta_1_hat:</strong></p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        dS/d(beta_1_hat) = -2 * Sum[ X_i * (Y_i - beta_0_hat - beta_1_hat * X_i) ] = 0<br />
        Sum(X_i * Y_i) - beta_0_hat * Sum(X_i) - beta_1_hat * Sum(X_i^2) = 0
      </div>

      <p>Substitute beta_0_hat = Y_bar - beta_1_hat * X_bar into Normal Equation 2:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        Sum(X_i * Y_i) - (Y_bar - beta_1_hat * X_bar) * Sum(X_i) - beta_1_hat * Sum(X_i^2) = 0<br />
        Sum(X_i * Y_i) - n * X_bar * Y_bar = beta_1_hat * [ Sum(X_i^2) - n * (X_bar)^2 ]
      </div>

      <p>Expressing in lower-case deviation form where <strong>x_i = X_i - X_bar</strong> and <strong>y_i = Y_i - Y_bar</strong>:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        beta_1_hat = Sum(x_i * y_i) / Sum(x_i^2) = Sample Cov(X, Y) / Sample Var(X)
      </div>

      <h3>2.2 Algebraic Properties of OLS Residuals</h3>
      <ol className="list-decimal list-inside space-y-2 text-sm my-4">
        <li><strong>Sum of residuals is zero:</strong> Sum(e_i) = 0 (and hence the mean of residuals e_bar = 0).</li>
        <li><strong>Residuals are orthogonal to X:</strong> Sum(X_i * e_i) = 0.</li>
        <li><strong>Residuals are orthogonal to fitted values:</strong> Sum(Y_hat_i * e_i) = 0.</li>
        <li><strong>The regression line passes through the sample means:</strong> (X_bar, Y_bar) always lies on the fitted line.</li>
      </ol>

      <h2>3. Classical Assumptions of the OLS Model (CLRM)</h2>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Assumption</th>
              <th className="p-3 border-r border-rule">Mathematical Statement</th>
              <th className="p-3">Economic Interpretation</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">A1: Linearity in Parameters</td>
              <td className="p-3 border-r border-rule font-mono text-xs">Y = beta_0 + beta_1*X + u</td>
              <td className="p-3">The model is linear in coefficients beta, even if X or Y are non-linear (e.g. log(Y) = beta_0 + beta_1*log(X)).</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">A2: Zero Conditional Mean</td>
              <td className="p-3 border-r border-rule font-mono text-xs">E[u_i | X_i] = 0</td>
              <td className="p-3 font-semibold text-grade">Exogeneity. The error term contains no systematic information related to X. Violation causes Endogeneity / Omitted Variable Bias.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">A3: Homoskedasticity</td>
              <td className="p-3 border-r border-rule font-mono text-xs">Var(u_i | X_i) = sigma^2 (Constant)</td>
              <td className="p-3">Equal error variance across all observations. Violation is Heteroskedasticity.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">A4: No Autocorrelation</td>
              <td className="p-3 border-r border-rule font-mono text-xs">Cov(u_i, u_j | X) = 0 for i != j</td>
              <td className="p-3">Errors across different observations are uncorrelated. Critical in time-series data.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">A5: Variation in X</td>
              <td className="p-3 border-r border-rule font-mono text-xs">Sum(x_i^2) &gt; 0, not all X_i are identical</td>
              <td className="p-3">X must take at least two distinct values in the sample. In multiple regression: No Perfect Multicollinearity.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">A6: Normality (for inference)</td>
              <td className="p-3 border-r border-rule font-mono text-xs">u_i ~ i.i.d. Normal(0, sigma^2)</td>
              <td className="p-3">Required for exact finite-sample t-tests and F-tests.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>4. The Gauss-Markov Theorem: BLUE Proof</h2>
      <p>
        <strong>Theorem Statement:</strong> Under Assumptions A1 to A5, the OLS estimators beta_0_hat and beta_1_hat are <strong>BLUE (Best Linear Unbiased Estimators)</strong>. That is, among all linear and unbiased estimators, OLS estimators have the <strong>minimum sampling variance</strong>.
      </p>

      <h3>4.1 Proof of Unbiasedness: E[beta_1_hat] = beta_1</h3>
      <p>Define the weight <strong>w_i = x_i / Sum(x_i^2)</strong>. Notice that:</p>
      <ul>
        <li>Sum(w_i) = Sum(x_i) / Sum(x_i^2) = 0  (since sum of deviations from mean is zero)</li>
        <li>Sum(w_i * X_i) = Sum(w_i * x_i) = Sum(x_i^2) / Sum(x_i^2) = 1</li>
      </ul>

      <p>Now write beta_1_hat as a linear combination of Y_i:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        beta_1_hat = Sum(w_i * Y_i) = Sum[ w_i * (beta_0 + beta_1 * X_i + u_i) ]<br />
        = beta_0 * Sum(w_i) + beta_1 * Sum(w_i * X_i) + Sum(w_i * u_i)<br />
        = beta_0 * (0) + beta_1 * (1) + Sum(w_i * u_i)<br />
        = beta_1 + Sum(w_i * u_i)
      </div>

      <p>Take conditional expectation on X:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        E[beta_1_hat | X] = beta_1 + Sum[ w_i * E(u_i | X) ] = beta_1 + Sum[ w_i * 0 ] = beta_1
      </div>
      <p>Hence, beta_1_hat is strictly <strong>Unbiased</strong>.</p>

      <h3>4.2 Variance of OLS Estimator</h3>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        Var(beta_1_hat | X) = Var[ Sum(w_i * u_i) ] = Sum[ w_i^2 * Var(u_i) ] = sigma^2 * Sum(w_i^2)<br />
        = sigma^2 * [ Sum(x_i^2) / (Sum(x_i^2))^2 ] = sigma^2 / Sum(x_i^2)
      </div>

      <h3>4.3 Proof of Minimum Variance (Efficiency)</h3>
      <p>
        Let beta_1_tilde = Sum(c_i * Y_i) be any other linear unbiased estimator of beta_1. Let c_i = w_i + d_i.
      </p>
      <p>For beta_1_tilde to be unbiased, we require Sum(c_i) = 0 and Sum(c_i * X_i) = 1, which implies Sum(d_i) = 0 and Sum(d_i * x_i) = 0.</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        Var(beta_1_tilde) = sigma^2 * Sum(c_i^2) = sigma^2 * Sum[ (w_i + d_i)^2 ]<br />
        = sigma^2 * [ Sum(w_i^2) + Sum(d_i^2) + 2 * Sum(w_i * d_i) ]
      </div>
      <p>Note that Sum(w_i * d_i) = [ 1 / Sum(x_i^2) ] * Sum(x_i * d_i) = 0. Therefore:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        Var(beta_1_tilde) = sigma^2 * Sum(w_i^2) + sigma^2 * Sum(d_i^2) = Var(beta_1_hat) + sigma^2 * Sum(d_i^2) &gt;= Var(beta_1_hat)
      </div>
      <p>
        Because Sum(d_i^2) &gt;= 0, the variance of any competing linear unbiased estimator is strictly greater than or equal to OLS variance. Equality holds if and only if d_i = 0 for all i (i.e., beta_1_tilde = beta_1_hat). <strong>Q.E.D. (OLS is BLUE).</strong>
      </p>

      <h2>5. Goodness of Fit: R-Squared (R^2) &amp; ANOVA</h2>
      <p>Total variation in Y is partitioned into Explained and Unexplained components:</p>

      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        TSS (Total Sum of Squares) = ESS (Explained Sum of Squares) + RSS (Residual Sum of Squares)<br />
        Sum[ (Y_i - Y_bar)^2 ] = Sum[ (Y_hat_i - Y_bar)^2 ] + Sum(e_i^2)
      </div>

      <p>The <strong>Coefficient of Determination (R^2)</strong> measures the proportion of total variation in Y explained by the regression line:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        R^2 = ESS / TSS = 1 - (RSS / TSS) = (beta_1_hat)^2 * Sum(x_i^2) / Sum(y_i^2)
      </div>
      <p>In simple bivariate regression, R^2 is exactly equal to the square of Pearson's correlation coefficient: <strong>R^2 = r_XY^2</strong>.</p>

      <h2>6. Worked Examination Numerical Problems</h2>

      <h3>Numerical Problem 1: Full Bivariate OLS Estimation (15 Marks)</h3>
      <p>
        <strong>Question:</strong> A study examines the relationship between study hours per week (X) and exam marks out of 100 (Y) for n = 5 students:<br />
        Student 1: X = 2, Y = 50<br />
        Student 2: X = 4, Y = 60<br />
        Student 3: X = 6, Y = 70<br />
        Student 4: X = 8, Y = 75<br />
        Student 5: X = 10, Y = 95<br /><br />
        (a) Compute OLS estimates beta_0_hat and beta_1_hat.<br />
        (b) Compute the sample residual variance s^2 and Standard Error of beta_1_hat.<br />
        (c) Test H0: beta_1 = 0 vs H1: beta_1 != 0 at the 5% significance level (t_critical for df=3 is 3.182).<br />
        (d) Compute R^2.
      </p>

      <h4>Solution:</h4>
      <p><strong>Step 1: Compute Summary Statistics</strong></p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        n = 5<br />
        Sum(X) = 2 + 4 + 6 + 8 + 10 = 30  ==&gt;  X_bar = 30/5 = 6<br />
        Sum(Y) = 50 + 60 + 70 + 75 + 95 = 350  ==&gt;  Y_bar = 350/5 = 70<br />
        Sum(X^2) = 4 + 16 + 36 + 64 + 100 = 220<br />
        Sum(x_i^2) = Sum(X^2) - n*(X_bar)^2 = 220 - 5*(36) = 220 - 180 = 40<br />
        Sum(X*Y) = 2*50 + 4*60 + 6*70 + 8*75 + 10*95 = 100 + 240 + 420 + 600 + 950 = 2,310<br />
        Sum(x_i * y_i) = Sum(X*Y) - n*X_bar*Y_bar = 2,310 - 5*(6)*(70) = 2,310 - 2,100 = 210
      </div>

      <p><strong>Part (a): OLS Coefficients</strong></p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        beta_1_hat = Sum(x_i * y_i) / Sum(x_i^2) = 210 / 40 = 5.25<br />
        beta_0_hat = Y_bar - beta_1_hat * X_bar = 70 - 5.25 * 6 = 70 - 31.50 = 38.50<br />
        Fitted Line: Y_hat = 38.50 + 5.25 * X
      </div>

      <p><strong>Part (b): Residuals, Variance s^2, and SE(beta_1_hat)</strong></p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        Y_hat values: 49.0, 59.5, 70.0, 80.5, 91.0<br />
        Residuals e_i = Y - Y_hat: +1.0, +0.5, 0.0, -5.5, +4.0<br />
        Sum(e_i^2) = 1.0^2 + 0.5^2 + 0.0^2 + (-5.5)^2 + 4.0^2 = 1.0 + 0.25 + 0 + 30.25 + 16.0 = 47.50<br />
        Unbiased error variance s^2 = RSS / (n - 2) = 47.50 / (5 - 2) = 47.50 / 3 = 15.833<br />
        s = sqrt(15.833) = 3.979<br />
        SE(beta_1_hat) = sqrt[ s^2 / Sum(x_i^2) ] = sqrt[ 15.833 / 40 ] = sqrt(0.3958) = 0.629
      </div>

      <p><strong>Part (c): Hypothesis Testing (t-test)</strong></p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        t_calculated = (beta_1_hat - 0) / SE(beta_1_hat) = 5.25 / 0.629 = 8.347<br />
        Since |t_calc| = 8.35 &gt; t_crit (3.182), we REJECT the null hypothesis H0 at the 5% level.<br />
        Conclusion: Study hours have a statistically significant positive effect on exam marks.
      </div>

      <p><strong>Part (d): Coefficient of Determination R^2</strong></p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        Sum(Y^2) = 2,500 + 3,600 + 4,900 + 5,625 + 9,025 = 25,650<br />
        TSS = Sum(Y^2) - n*(Y_bar)^2 = 25,650 - 5*(4,900) = 25,650 - 24,500 = 1,150<br />
        ESS = TSS - RSS = 1,150 - 47.50 = 1,102.50<br />
        R^2 = ESS / TSS = 1,102.50 / 1,150 = 0.9587 (or 95.87%)
      </div>

      <h3>Numerical Problem 2: ANOVA Table &amp; F-Test (10 Marks)</h3>
      <p>
        <strong>Question:</strong> For a regression with n = 22 observations and k = 3 explanatory variables (X1, X2, X3), total sum of squares TSS = 500 and residual sum of squares RSS = 100.<br />
        (a) Construct the ANOVA Table.<br />
        (b) Test the overall significance of the regression at the 1% level (F_critical for df=(3, 18) is 5.09).
      </p>

      <h4>Solution:</h4>
      <div className="overflow-x-auto my-4">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Source of Variation</th>
              <th className="p-3 border-r border-rule">Sum of Squares (SS)</th>
              <th className="p-3 border-r border-rule">Degrees of Freedom (df)</th>
              <th className="p-3 border-r border-rule">Mean Square (MS)</th>
              <th className="p-3">F-Statistic</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">Regression (ESS)</td>
              <td className="p-3 border-r border-rule">400 (500 - 100)</td>
              <td className="p-3 border-r border-rule">k = 3</td>
              <td className="p-3 border-r border-rule">400 / 3 = 133.33</td>
              <td className="p-3 font-semibold text-grade" rowSpan={2}>F = 133.33 / 5.56 = 24.00</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Residual (RSS)</td>
              <td className="p-3 border-r border-rule">100</td>
              <td className="p-3 border-r border-rule">n - k - 1 = 22 - 3 - 1 = 18</td>
              <td className="p-3 border-r border-rule">100 / 18 = 5.56</td>
            </tr>
            <tr className="font-semibold bg-card">
              <td className="p-3 border-r border-rule">Total (TSS)</td>
              <td className="p-3 border-r border-rule">500</td>
              <td className="p-3 border-r border-rule">n - 1 = 21</td>
              <td className="p-3 border-r border-rule">-</td>
              <td className="p-3">-</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p>
        <strong>Conclusion:</strong> Since F_calculated = 24.00 &gt; F_critical = 5.09, we reject H0: beta_1 = beta_2 = beta_3 = 0. The regression model is jointly highly significant at the 1% level.
      </p>

      <h2>7. Summary Checklist for Econometrics Exams</h2>
      <ul>
        <li>State all 5 Gauss-Markov assumptions clearly before writing proofs.</li>
        <li>Remember the derivation shorthand: beta_1_hat = Sum(w_i * Y_i) where w_i = x_i / Sum(x_i^2).</li>
        <li>Degrees of freedom for residual variance in SLRM is always <strong>n - 2</strong>. In multiple regression with k slopes, df = <strong>n - k - 1</strong>.</li>
        <li>R^2 increases monotonically whenever a new variable is added, which is why Adjusted R^2 is preferred for model comparison.</li>
      </ul>

      <p>
        For related mathematical economics guides, see our <a href="/blog/mme-du-economics-guide" className="font-semibold underline text-grade">MME Distinction Masterclass</a> and <a href="/blog/cobb-douglas-production-function-proofs" className="font-semibold underline text-grade">Cobb-Douglas Mathematical Proofs</a>.
      </p>
    </>
  );
}


